import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { Quiz, QuizQuestion } from '../../lib/quizzes';
import { calculateQuizResult, CalculatedResult } from '../../lib/personalityQuiz';
import useLocalStorage from '../../lib/useLocalStorage';
import { fadeInUp, fadeIn, scaleIn } from '../../lib/animations';

interface QuizEngineProps {
  quiz: Quiz;
  onComplete: (result: CalculatedResult, userName: string) => void;
}

interface HistoryEntry {
  quizSlug: string;
  quizTitle: string;
  userName: string;
  resultShortName: string;
  resultPersona: string;
  finishedAt: string;
}

const QuizEngine: React.FC<QuizEngineProps> = ({ quiz, onComplete }) => {
  const [userName, setUserName] = useLocalStorage<string>('quiz:username', '');
  const [history, setHistory] = useLocalStorage<HistoryEntry[]>('quiz:history', []);

  // Engine states: 'welcome' | 'questions' | 'loading'
  const [step, setStep] = useState<'welcome' | 'questions' | 'loading'>('welcome');
  const [inputName, setInputName] = useState(userName);
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({}); // questionId -> selectedOptionText
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Initialize input name if userName changes
  useEffect(() => {
    setInputName(userName);
  }, [userName]);

  // Handle loading progress bar simulation
  useEffect(() => {
    if (step !== 'loading') return;

    const interval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          handleFinishQuiz();
          return 100;
        }
        return prev + 5;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [step]);

  const handleStart = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputName.trim()) return;

    setUserName(inputName.trim());
    setStep('questions');
  };

  const handleAnswerSelect = (optionText: string) => {
    const question = quiz.questions[currentQuestionIdx];
    setAnswers((prev) => ({
      ...prev,
      [question.id]: optionText,
    }));

    // Auto advance to next question with a slight delay for better feel
    setTimeout(() => {
      if (currentQuestionIdx < quiz.questions.length - 1) {
        setCurrentQuestionIdx((prev) => prev + 1);
      } else {
        setStep('loading');
      }
    }, 250);
  };

  const handleBack = () => {
    if (currentQuestionIdx > 0) {
      setCurrentQuestionIdx((prev) => prev - 1);
    } else {
      setStep('welcome');
    }
  };

  const handleFinishQuiz = () => {
    // Transform answers into array format
    const selectedAnswers = Object.entries(answers).map(([id, text]) => ({
      questionId: Number(id),
      selectedOptionText: text,
    }));

    const result = calculateQuizResult(quiz, selectedAnswers);

    // Save to local history
    const newHistoryEntry: HistoryEntry = {
      quizSlug: quiz.slug,
      quizTitle: quiz.title,
      userName: inputName.trim(),
      resultShortName: result.shortName,
      resultPersona: result.persona,
      finishedAt: new Date().toISOString(),
    };

    setHistory((prev) => [newHistoryEntry, ...prev]);
    onComplete(result, inputName.trim());
  };

  const activeQuestion = quiz.questions[currentQuestionIdx];
  const progressPercent = Math.round(((currentQuestionIdx) / quiz.questions.length) * 100);

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
      <AnimatePresence mode="wait">
        {/* Step: Welcome / Name Input */}
        {step === 'welcome' && (
          <motion.div
            key="welcome"
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="rounded-3xl bg-slate-900 border border-slate-800 p-8 sm:p-12 shadow-2xl text-center"
          >
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-white mb-4 leading-snug">
              Pronto para começar?
            </h2>
            <p className="text-slate-400 text-sm sm:text-base leading-relaxed mb-8">
              Para salvar e compartilhar o seu resultado com o seu nome, digite-o no campo abaixo.
            </p>

            <form onSubmit={handleStart} className="space-y-4 max-w-md mx-auto">
              <div>
                <label htmlFor="name-input" className="sr-only">
                  Seu nome ou apelido
                </label>
                <input
                  type="text"
                  id="name-input"
                  required
                  placeholder="Digite seu nome ou apelido..."
                  value={inputName}
                  onChange={(e) => setInputName(e.target.value)}
                  className="w-full rounded-2xl bg-slate-950 border border-slate-800 hover:border-slate-700 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 text-white font-medium px-5 py-4 text-center placeholder-slate-600 transition outline-none"
                />
              </div>

              <button
                type="submit"
                disabled={!inputName.trim()}
                className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-amber-400 hover:bg-amber-300 disabled:opacity-50 disabled:hover:bg-amber-400 text-slate-950 font-bold px-6 py-4 transition-all duration-200 shadow-lg shadow-amber-400/10 cursor-pointer"
              >
                Iniciar Teste
                <Play className="h-4 w-4 fill-current" />
              </button>
            </form>
          </motion.div>
        )}

        {/* Step: Interactive Questions */}
        {step === 'questions' && activeQuestion && (
          <motion.div
            key="questions"
            variants={fadeIn}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="space-y-6"
          >
            {/* Progress indicator */}
            <div className="flex items-center justify-between text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
              <button
                onClick={handleBack}
                className="flex items-center gap-1.5 hover:text-white transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </button>
              <span>
                Pergunta {currentQuestionIdx + 1} de {quiz.questions.length}
              </span>
            </div>

            {/* Progress bar */}
            <div className="h-2 w-full rounded-full bg-slate-900 overflow-hidden border border-slate-800">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 0.3 }}
                className="h-full bg-amber-400 rounded-full"
              />
            </div>

            {/* Question Card */}
            <motion.div
              key={activeQuestion.id}
              variants={scaleIn}
              initial="hidden"
              animate="visible"
              className="rounded-3xl bg-slate-900 border border-slate-800 p-6 sm:p-10 shadow-2xl space-y-6"
            >
              <h2 className="font-display text-xl sm:text-2xl font-bold text-white leading-snug">
                {activeQuestion.question}
              </h2>

              <div className="space-y-3">
                {activeQuestion.options.map((option, idx) => {
                  const isSelected = answers[activeQuestion.id] === option.text;
                  return (
                    <button
                      key={idx}
                      onClick={() => handleAnswerSelect(option.text)}
                      className={`w-full text-left rounded-2xl p-5 border text-sm sm:text-base font-medium transition-all duration-200 leading-relaxed cursor-pointer ${
                        isSelected
                          ? 'bg-amber-400/10 border-amber-400 text-amber-300 shadow-lg shadow-amber-400/5'
                          : 'bg-slate-950/40 border-slate-800/80 text-slate-300 hover:bg-slate-950/80 hover:border-slate-700 hover:text-white'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-bold ${
                          isSelected
                            ? 'bg-amber-400 border-amber-400 text-slate-950'
                            : 'border-slate-800 text-slate-500 bg-slate-950'
                        }`}>
                          {String.fromCharCode(65 + idx)}
                        </span>
                        <span>{option.text}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Step: Simulating Scoring/Calculation Loading screen */}
        {step === 'loading' && (
          <motion.div
            key="loading"
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            className="rounded-3xl bg-slate-900 border border-slate-800 p-10 sm:p-14 shadow-2xl text-center space-y-8"
          >
            <div className="mx-auto flex h-16 w-14 items-center justify-center">
              <Loader2 className="h-10 w-10 text-amber-400 animate-spin" />
            </div>

            <div className="space-y-3">
              <h2 className="font-display text-xl sm:text-2xl font-extrabold text-white">
                Calculando seu resultado...
              </h2>
              <p className="text-slate-400 text-sm leading-relaxed max-w-sm mx-auto">
                Analisando suas respostas para encontrar a sua verdadeira personalidade automotiva!
              </p>
            </div>

            {/* Custom progress loading bar */}
            <div className="max-w-xs mx-auto space-y-2">
              <div className="h-2 w-full rounded-full bg-slate-950 overflow-hidden border border-slate-800">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${loadingProgress}%` }}
                  className="h-full bg-amber-400 rounded-full"
                />
              </div>
              <span className="text-xs font-bold text-amber-400">{loadingProgress}%</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuizEngine;
